/** Vehicle fields that can make up an Initial Price Estimate search term. Sent and stored by name. */
export type PriceEstimateSearchField =
  | "Make"
  | "Model"
  | "Series"
  | "Year"
  | "EngineCapacity"
  | "EngineCode"
  | "FuelType"
  | "BodyStyle"
  | "Transmission";

export const SEARCH_FIELD_LABELS: Record<PriceEstimateSearchField, string> = {
  Year: "Year",
  Make: "Make",
  Model: "Model",
  Series: "Series / Mk",
  EngineCapacity: "Engine Size",
  EngineCode: "Engine Code",
  FuelType: "Fuel Type",
  BodyStyle: "Body Style",
  Transmission: "Transmission",
};

/** Display order of the chips, which is not the search order - that is the order they are picked in. */
export const ALL_SEARCH_FIELDS = Object.keys(
  SEARCH_FIELD_LABELS,
) as PriceEstimateSearchField[];

export const DEFAULT_SEARCH_FIELDS: PriceEstimateSearchField[] = [
  "Year",
  "Make",
  "Model",
];

/** Same example vehicle as the API's InitialPriceEstimateRules.ExampleVehicle. */
export const EXAMPLE_VEHICLE: Record<PriceEstimateSearchField, string> = {
  Make: "Ford",
  Model: "Focus",
  Series: "MK3",
  Year: "2015",
  EngineCapacity: "1.6",
  EngineCode: "XTDA",
  FuelType: "Petrol",
  BodyStyle: "Hatchback",
  Transmission: "Manual",
};

/**
 * The search term for the example vehicle: fields in order, then the keyword, with no word repeated.
 * Mirrors the API (and Core's real search), so an unsaved edit previews exactly what will be searched.
 */
export function buildExampleSearchTerm(
  fields: PriceEstimateSearchField[],
  keyword: string | null,
): string {
  const seen = new Set<string>();
  const words: string[] = [];

  [...fields.map((f) => EXAMPLE_VEHICLE[f]), keyword].forEach((value) => {
    (value ?? "")
      .split(/\s+/)
      .filter(Boolean)
      .forEach((word) => {
        const key = word.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        words.push(word);
      });
  });

  return words.join(" ");
}

export interface IInitialPriceEstimateRequest {
  isActive: boolean;
  /** Blank means no keyword: the vehicle fields alone, narrowed by the eBay category. */
  searchKeyword: string | null;
  /** Blank means the category's own eBay category id. */
  ebayCategoryId: string | null;
  /** In search-term order. */
  searchFields: PriceEstimateSearchField[];
  sortOrder: number;
  /** This category's AI match prompt. Blank means the default prompt. */
  aiPrompt: string | null;
}

export interface IInitialPriceEstimateResponse
  extends IInitialPriceEstimateRequest {
  id: string | null;
  partCategoryId: string;
  categoryName: string;
  categoryEbayCategoryId: string | null;
  resolvedEbayCategoryId: string | null;
  exampleSearchTerm: string;
  /** The SerpApi query for that example search, with {{apiKey}} where the key goes. */
  exampleSerpQuery: string;
  /** What a blank AI prompt inherits: the saved default, or the built-in. */
  defaultAiPrompt: string;
  isSaved: boolean;
}

/**
 * The default AI match prompt: what the lookup's AI check is told when a category has no prompt
 * of its own. Global, like the Serp settings. Edited from the Initial Price Estimate page.
 */
export interface IAiResultFilterPrompt {
  /** The prompt in use: the saved one, or the built-in. */
  prompt: string;
  /** The built-in prompt, for a reset. */
  builtInPrompt: string;
  /** True when nothing has been saved and the built-in prompt is in use. */
  usingBuiltIn: boolean;
}

export interface ISaveAiResultFilterPromptRequest {
  /** Blank goes back to the built-in prompt. */
  prompt: string | null;
}

/** Matches the API's AiResultFilterRules.MaxPromptLength. */
export const MAX_AI_PROMPT_LENGTH = 8000;

export interface IInitialPriceEstimateCategorySummary {
  partCategoryId: string;
  name: string;
  isActive: boolean;
  isConfigured: boolean;
  sortOrder: number;
  /** Null when the category searches on the vehicle fields alone. */
  searchKeyword: string | null;
  /** The SerpApi query this category runs for the example vehicle. Null when not priced. */
  exampleSerpQuery: string | null;
}
